import { useRef, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft, Users, ListChecks, UserPlus, Plus, Trash2, AlertTriangle, MessageSquare,
  FileText, Image as ImageIcon, Film, Music, Download, Upload, Paperclip,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import Modal from '@/components/Modal';
import { StatusBadge, PriorityBadge } from '@/components/Badge';
import Loader from '@/components/Loader';
import ConfirmDialog from '@/components/ConfirmDialog';
import { projectService, userService, taskService, fileUrl } from '@/services';
import { useAuth } from '@/hooks/useAuth';
import { fmtDate, fmtDateTime } from '@/utils/format';

function bytesToHuman(n = 0) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function docIcon(kind) {
  if (kind === 'image') return ImageIcon;
  if (kind === 'video') return Film;
  if (kind === 'audio') return Music;
  return FileText;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['project', id], queryFn: () => projectService.get(id) });

  if (isLoading) return <Loader />;
  if (!data?.project) return <div>Project not found</div>;

  const p = data.project;
  const tasks = data.tasks || [];
  const canDelete = isAdmin;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Link to="/projects" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-600">
          <ArrowLeft size={14} /> Back to projects
        </Link>
        <div className="flex items-center gap-1">
          <Link
            to={`/projects/${p._id}/chat`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 hover:bg-brand-50 dark:hover:bg-brand-500/10 px-3 py-1.5 rounded-lg transition"
            title="Open project chat"
          >
            <MessageSquare size={14} /> Open chat
          </Link>
          {canDelete && (
            <button
              onClick={() => setShowDelete(true)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-500/10 px-3 py-1.5 rounded-lg transition"
              title="Delete project"
            >
              <Trash2 size={14} /> Delete project
            </button>
          )}
        </div>
      </div>

      <PageHeader
        title={p.name}
        subtitle={p.code}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={p.status} />
            <PriorityBadge priority={p.priority} />
          </div>
        }
      />

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <div className="font-semibold mb-2">About</div>
          <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{p.description || '—'}</p>
          <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
            <div><div className="text-slate-500 text-xs">Client</div><div className="font-medium">{p.client || '—'}</div></div>
            <div><div className="text-slate-500 text-xs">Manager</div><div className="font-medium">{p.manager?.name || '—'}</div></div>
            <div><div className="text-slate-500 text-xs">Created by</div><div className="font-medium">{p.createdBy?.name || '—'}</div></div>
            <div><div className="text-slate-500 text-xs">Start</div><div className="font-medium">{fmtDate(p.startDate)}</div></div>
            <div><div className="text-slate-500 text-xs">End</div><div className="font-medium">{fmtDate(p.endDate)}</div></div>
          </div>
          <div className="mt-4">
            <div className="text-xs text-slate-500 mb-1">Overall progress</div>
            <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-brand-500 to-indigo-500" style={{ width: `${p.progress || 0}%` }} />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold flex items-center gap-2"><Users size={16} /> Team ({(p.members || []).length})</div>
            <button className="btn-secondary px-2 py-1 text-xs" onClick={() => setShowAdd(true)}>
              <UserPlus size={12} /> Add member
            </button>
          </div>
          <ul className="space-y-2">
            {(p.members || []).map((m) => (
              <li key={m._id} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center text-sm font-semibold">
                  {m.name?.[0]?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{m.name}</div>
                  <div className="text-xs text-slate-500 truncate">{m.email}</div>
                </div>
              </li>
            ))}
            {(p.members || []).length === 0 && <div className="text-sm text-slate-500">No members yet</div>}
          </ul>
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold flex items-center gap-2"><ListChecks size={16} /> Tasks ({tasks.length})</div>
          <button className="btn-primary px-3 py-1.5 text-sm" onClick={() => setShowAddTask(true)}>
            <Plus size={14} /> Add task
          </button>
        </div>
        {tasks.length === 0 ? (
          <div className="text-sm text-slate-500 py-4">No tasks for this project yet</div>
        ) : (
          <div className="table-wrap">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="th">Title</th>
                  <th className="th">Assignee</th>
                  <th className="th">Assigned by</th>
                  <th className="th">Deadline</th>
                  <th className="th">Priority</th>
                  <th className="th">Status</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => (
                  <tr
                    key={t._id}
                    onClick={() => navigate(`/tasks/${t._id}`)}
                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <td className="td font-medium">{t.title}</td>
                    <td className="td">{t.assignee?.name || '—'}</td>
                    <td className="td">{t.createdBy?.name || '—'}</td>
                    <td className="td">{t.deadline ? fmtDate(t.deadline) : '—'}</td>
                    <td className="td"><PriorityBadge priority={t.priority} /></td>
                    <td className="td"><StatusBadge status={t.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ProjectDocuments project={p} onChanged={() => qc.invalidateQueries({ queryKey: ['project', id] })} />

      {showAdd && (
        <AddMembersModal
          project={p}
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            setShowAdd(false);
            qc.invalidateQueries({ queryKey: ['project', id] });
            qc.invalidateQueries({ queryKey: ['projects'] });
          }}
        />
      )}

      {showAddTask && (
        <AddTaskModal
          project={p}
          onClose={() => setShowAddTask(false)}
          onCreated={() => {
            setShowAddTask(false);
            qc.invalidateQueries({ queryKey: ['project', id] });
            qc.invalidateQueries({ queryKey: ['tasks'] });
          }}
        />
      )}

      {showDelete && (
        <DeleteProjectModal
          project={p}
          taskCount={tasks.length}
          onClose={() => setShowDelete(false)}
          onDeleted={() => {
            qc.invalidateQueries({ queryKey: ['projects'] });
            qc.removeQueries({ queryKey: ['project', id] });
            navigate('/projects');
          }}
        />
      )}
    </div>
  );
}

function DeleteProjectModal({ project, taskCount, onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const expected = project.name;

  const doDelete = async () => {
    setDeleting(true);
    try {
      await projectService.remove(project._id);
      toast.success('Project deleted');
      onDeleted?.();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Delete project"
      size="md"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={doDelete}
            className="btn-danger"
            disabled={deleting || confirmText !== expected}
          >
            <Trash2 size={14} /> {deleting ? 'Deleting...' : 'Delete project'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30">
          <AlertTriangle className="text-rose-600 shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-rose-700 dark:text-rose-300">
            This will permanently delete <b>{project.name}</b>
            {taskCount > 0 && (
              <> and all <b>{taskCount}</b> task{taskCount === 1 ? '' : 's'} attached to it</>
            )}. This action cannot be undone.
          </div>
        </div>
        <div>
          <label className="label">
            Type <span className="font-mono text-rose-600">{expected}</span> to confirm
          </label>
          <input
            className="input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={expected}
            autoFocus
          />
        </div>
      </div>
    </Modal>
  );
}

function AddMembersModal({ project, onClose, onAdded }) {
  const users = useQuery({ queryKey: ['users', 'all'], queryFn: () => userService.list({ limit: 200 }) });
  const existingIds = useMemo(
    () => new Set((project.members || []).map((m) => String(m._id || m))),
    [project]
  );
  const candidates = (users.data?.items || []).filter((u) => !existingIds.has(String(u._id)));
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    setSelected((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));
  };

  const filtered = candidates.filter((u) =>
    !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase())
  );

  const save = async () => {
    if (selected.length === 0) {
      toast.error('Select at least one teammate');
      return;
    }
    setSaving(true);
    try {
      const res = await projectService.addMembers(project._id, selected);
      toast.success(`${res.added} member${res.added === 1 ? '' : 's'} added`);
      onAdded?.();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Add members to ${project.name}`}
      size="lg"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={save} className="btn-primary" disabled={saving || selected.length === 0}>
            {saving ? 'Adding...' : `Add ${selected.length || ''}`}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <input
          className="input"
          placeholder="Search teammates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="max-h-80 overflow-y-auto border border-slate-200 dark:border-slate-800 rounded-lg divide-y divide-slate-100 dark:divide-slate-800">
          {filtered.length === 0 && (
            <div className="p-4 text-sm text-slate-500 text-center">
              {candidates.length === 0 ? 'Everyone is already on this project.' : 'No teammates match your search.'}
            </div>
          )}
          {filtered.map((u) => (
            <label key={u._id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <input
                type="checkbox"
                className="rounded"
                checked={selected.includes(u._id)}
                onChange={() => toggle(u._id)}
              />
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center text-sm font-semibold">
                {u.name?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{u.name}</div>
                <div className="text-xs text-slate-500 truncate">{u.email} · {u.department}</div>
              </div>
            </label>
          ))}
        </div>
      </div>
    </Modal>
  );
}

function AddTaskModal({ project, onClose, onCreated }) {
  const { user } = useAuth();
  const { register, handleSubmit, reset, setValue, watch, formState: { isSubmitting } } = useForm({
    defaultValues: { priority: 'medium', status: 'pending', assignee: '' },
  });
  const users = useQuery({ queryKey: ['users', 'all'], queryFn: () => userService.list({ limit: 200 }) });

  const memberIds = useMemo(() => new Set((project.members || []).map((m) => String(m._id || m))), [project]);
  const allUsers = users.data?.items || [];
  const sortedUsers = useMemo(() => {
    const inProject = [];
    const others = [];
    for (const u of allUsers) {
      if (memberIds.has(String(u._id))) inProject.push(u);
      else others.push(u);
    }
    return { inProject, others };
  }, [allUsers, memberIds]);

  const currentAssignee = watch('assignee');

  const onSubmit = async (data) => {
    try {
      const payload = {
        project: project._id,
        title: data.title,
        description: data.description,
        priority: data.priority,
        status: data.status,
        assignee: data.assignee || undefined,
        deadline: data.deadline || undefined,
        estimatedHours: data.estimatedHours ? Number(data.estimatedHours) : 0,
        completionPercent: 0,
      };
      await taskService.create(payload);
      toast.success('Task added');
      reset();
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Add task — ${project.name}`}
      size="lg"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button form="addTaskForm" type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Add task'}
          </button>
        </>
      }
    >
      <form id="addTaskForm" onSubmit={handleSubmit(onSubmit)} className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="label">Title</label>
          <input className="input" {...register('title', { required: true })} placeholder="What needs to be done?" />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Description</label>
          <textarea rows={3} className="input" {...register('description')} />
        </div>
        <div className="sm:col-span-2">
          <div className="flex items-center justify-between">
            <label className="label">Assignee</label>
            {user?._id && (
              <button
                type="button"
                onClick={() => setValue('assignee', user._id, { shouldDirty: true })}
                className="text-xs text-brand-600 hover:underline"
              >
                Assign to me
              </button>
            )}
          </div>
          <select className="input" {...register('assignee')}>
            <option value="">Unassigned</option>
            {sortedUsers.inProject.length > 0 && (
              <optgroup label="Project members">
                {sortedUsers.inProject.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.name}{u._id === user?._id ? ' (You)' : ''}{u.department ? ` — ${u.department}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            {sortedUsers.others.length > 0 && (
              <optgroup label="Other teammates">
                {sortedUsers.others.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.name}{u._id === user?._id ? ' (You)' : ''}{u.department ? ` — ${u.department}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {currentAssignee === user?._id && (
            <p className="text-xs text-slate-500 mt-1">You'll own this task.</p>
          )}
        </div>
        <div>
          <label className="label">Priority</label>
          <select className="input" {...register('priority')}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        <div>
          <label className="label">Status</label>
          <select className="input" {...register('status')}>
            <option value="pending">Pending</option>
            <option value="in-progress">In progress</option>
            <option value="on-hold">On hold</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <div>
          <label className="label">Deadline <span className="text-slate-400 font-normal">(optional)</span></label>
          <input type="date" className="input" {...register('deadline')} />
        </div>
        <div>
          <label className="label">Estimated hours</label>
          <input type="number" min={0} step="0.5" className="input" {...register('estimatedHours')} />
        </div>
      </form>
    </Modal>
  );
}

function ProjectDocuments({ project, onChanged }) {
  const { user, isAdmin } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const docs = useMemo(() => {
    const list = [...(project.documents || [])];
    list.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
    return list;
  }, [project.documents]);

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append('documents', f));
    setUploading(true);
    try {
      await projectService.addDocuments(project._id, fd);
      toast.success(`${files.length} document${files.length === 1 ? '' : 's'} uploaded`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = (doc) => {
    setConfirmState({
      title: 'Delete document',
      message: `“${doc.filename}” will be permanently removed from this project.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setRemovingId(doc._id);
        try {
          await projectService.removeDocument(project._id, doc._id);
          toast.success('Document removed');
          onChanged?.();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Failed');
        } finally {
          setRemovingId(null);
        }
      },
    });
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="font-semibold flex items-center gap-2">
          <FileText size={16} /> Documents <span className="text-slate-400 font-normal text-sm">({docs.length})</span>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.7z,.csv,.json,.md"
            onChange={onPickFiles}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn-primary px-3 py-1.5 text-sm"
          >
            {uploading ? (
              <>
                <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={14} /> Upload
              </>
            )}
          </button>
        </div>
      </div>

      {docs.length === 0 ? (
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl py-8 text-center text-sm text-slate-500 hover:border-brand-400 hover:bg-brand-50/40 dark:hover:bg-brand-500/5 transition"
        >
          <Paperclip className="mx-auto mb-2" size={20} />
          Click to upload project documents
          <div className="text-[11px] text-slate-400 mt-1">Images, videos, PDFs, docs, archives — up to 25 MB each</div>
        </button>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {docs.map((d) => {
            const Icon = docIcon(d.kind);
            const isUploader = d.uploadedBy && String(d.uploadedBy._id || d.uploadedBy) === String(user?._id);
            const canDelete = isUploader || isAdmin;
            return (
              <li key={d._id} className="flex items-center gap-3 py-3">
                <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${
                  d.kind === 'image' ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400'
                  : d.kind === 'video' ? 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400'
                  : d.kind === 'audio' ? 'bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400'
                  : 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400'
                }`}>
                  <Icon size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <a
                    href={fileUrl(d.url)}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-sm truncate block hover:text-brand-600"
                    title={d.filename}
                  >
                    {d.filename}
                  </a>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap mt-0.5">
                    <span>{bytesToHuman(d.size)}</span>
                    <span className="text-slate-300 dark:text-slate-600">·</span>
                    <span>{fmtDateTime(d.uploadedAt)}</span>
                    {d.uploadedBy?.name && (
                      <>
                        <span className="text-slate-300 dark:text-slate-600">·</span>
                        <span>by {d.uploadedBy.name}</span>
                      </>
                    )}
                  </div>
                </div>
                <a
                  href={fileUrl(d.url)}
                  download={d.filename}
                  className="btn-ghost p-2 text-slate-500 hover:text-brand-600"
                  title="Download"
                >
                  <Download size={14} />
                </a>
                {canDelete && (
                  <button
                    onClick={() => removeDoc(d)}
                    disabled={removingId === d._id}
                    className="btn-ghost p-2 text-slate-500 hover:text-rose-500"
                    title={isUploader ? 'Delete (yours)' : 'Delete (admin)'}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </Card>
  );
}

